import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Camera, CircleDollarSign, Mail, Plus, SearchCheck, Star, Warehouse } from 'lucide-react';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { GlobalNavbar } from '../components/global-navbar';
import { OptInSection } from '../components/OptInSection';
import { blogPosts } from '../content/blogPosts';
import { cn } from '../utils/cn';
import { useSeo } from '../utils/useSeo';

const heroSectionImage = '/car-dealer-hero.png';
const dealsFinderImage = '/deals-finder.png';
const financeLaptopImage = '/finance-laptop.png';
const aiBeforeImage = '/before-optimized.jpeg';
const aiAfterImage = '/after.jpeg';

const trustAvatars = [
  { alt: 'Dealer profile 1', src: 'https://randomuser.me/api/portraits/men/31.jpg' },
  { alt: 'Dealer profile 2', src: 'https://randomuser.me/api/portraits/men/52.jpg' },
  { alt: 'Dealer profile 3', src: 'https://randomuser.me/api/portraits/men/73.jpg' },
] as const;

const dealerSliderLogos = [
  {
    alt: 'Dealership logo 1',
    src: 'https://framerusercontent.com/images/jfMP2UScQjpHeuQ9xXkay87VJug.png?width=384&height=121',
  },
  {
    alt: 'Dealership logo 2',
    src: 'https://framerusercontent.com/images/8wxNPikSyxKBdnKbbsRRhwNPY.png?width=168&height=180',
  },
  {
    alt: 'Dealership logo 3',
    src: 'https://framerusercontent.com/images/jOdkBJKqWraJEUg6S7xLjEQ6w0.png?width=384&height=97',
  },
  {
    alt: 'Dealership logo 4',
    src: 'https://framerusercontent.com/images/8YRbcNGvJs3jCProjjTsxZV0sc.png?scale-down-to=512&width=577&height=244',
  },
  {
    alt: 'Dealership logo 5',
    src: 'https://framerusercontent.com/images/zGVSvH3etgJ3i41i7Vo1dweItM.png?scale-down-to=512&width=744&height=167',
  },
  {
    alt: 'Dealership logo 6',
    src: 'https://framerusercontent.com/images/xBnNlDec3qSs8PgGjIIgeBHICdY.png?width=384&height=125',
  },
] as const;
const heroToolPills = ['Deals Finder', 'AI Photo', 'Garage', 'Finance'] as const;
const heroPillPositions: Record<(typeof heroToolPills)[number], string> = {
  'Deals Finder': 'left-2 top-[34%] -translate-x-[8%] -translate-y-1/2 sm:left-0 sm:-translate-x-[30%]',
  'AI Photo': 'left-2 top-[62%] -translate-x-[8%] -translate-y-1/2 sm:left-0 sm:-translate-x-[30%]',
  Garage: 'right-2 top-[44%] translate-x-[8%] -translate-y-1/2 sm:right-0 sm:translate-x-[30%]',
  Finance: 'right-2 top-[70%] translate-x-[8%] -translate-y-1/2 sm:right-0 sm:translate-x-[30%]',
};
const dealsFinderBenefits = [
  'Spot underpriced cars before your competition.',
  'Cut manual sourcing time and focus only on profitable deals.',
  'Move faster on buys and improve margin consistency.',
  'Turn sourcing into a repeatable system instead of guesswork.',
] as const;
const aiPhotographyBenefits = [
  'Cleaner, brighter car photos in one click.',
  'Consistent look across every listing.',
  'Better photos help justify higher asking prices.',
] as const;
const financeManagementBenefits = [
  'Track purchase price, repairs, fees, and final profit automatically',
  'See real margins per car — not rough estimates',
  'Export clean reports for accounting or partners in seconds',
  'Keep every deal documented and easy to review later',
] as const;

const faqItems = [
  {
    answer:
      'You set your target criteria and Profit Cars helps surface qualifying listings faster so you can act before competitors.',
    question: 'How does Deals Finder work?',
  },
  {
    answer:
      'Yes. You can track purchase price, repairs, transport, fees, and final sale in one flow so your margin stays accurate.',
    question: 'Can I track all costs per vehicle?',
  },
  {
    answer:
      'It helps standardize listing visuals and improve perceived value, which supports stronger asking prices and faster buyer response.',
    question: 'What does AI Photography improve?',
  },
  {
    answer:
      'No. It works for solo dealers and teams. You can start simple and scale your workflow as your monthly volume grows.',
    question: 'Is Profit Cars only for large dealerships?',
  },
  {
    answer:
      'Join through the access form and we will onboard you with the right setup based on your current sourcing and sales process.',
    question: 'How do I get access?',
  },
] as const;

const testimonials = [
  {
    avatar: 'https://unsplash.com/photos/fs2LuARz0k4/download?force=true&w=400',
    meta: 'Independent Dealer • Madrid',
    name: 'Carlos M.',
    quote:
      'Deals Finder alone makes this worth it. I see underpriced cars before other dealers in my area and can move on them immediately. My sourcing is much faster now.',
  },
  {
    avatar: 'https://unsplash.com/photos/SPQRHrKzX9c/download?force=true&w=400',
    meta: 'Used Car Buyer • Berlin',
    name: 'Lukas H.',
    quote:
      'Before Profit Cars we wasted hours checking listings manually. Now the system surfaces the deals that actually matter. We buy better cars and waste less time.',
  },
  {
    avatar: 'https://unsplash.com/photos/NIe0wtWcJE8/download?force=true&w=400',
    meta: 'Dealership Owner • Berlin',
    name: 'Marco D.',
    quote:
      'The finance tracking changed how we run the dealership. Every cost is recorded - purchase, transport, repairs. Now we see the real profit per car.',
  },
  {
    avatar: 'https://unsplash.com/photos/wkFCYbZ3ijg/download?force=true&w=400',
    meta: 'Independent Dealer • Barcelona',
    name: 'David R.',
    quote:
      'The photo tool makes every listing look professional. Cleaner photos mean better first impressions and we can hold stronger prices when buyers call.',
  },
  {
    avatar: 'https://unsplash.com/photos/-gZJJJV-TN4/download?force=true&w=400',
    meta: 'Small Dealer • Prague',
    name: 'Thomas K.',
    quote:
      "I don't run high volume, so every deal matters. Profit Cars helps me validate the numbers before buying so I don't get surprised later.",
  },
  {
    avatar: 'https://unsplash.com/photos/bBs7PyVWvAg/download?force=true&w=400',
    meta: 'Dealer Group Buyer • Vienna',
    name: 'Andreas V.',
    quote:
      'Our team now follows one clear process from sourcing to sale. Everything is tracked and we make decisions faster because the margins are clear.',
  },
] as const;
const benefitListVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.14,
    },
  },
};
const benefitItemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.34 },
  },
};
const tickIconVariants = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.28 },
  },
};
const tickPathVariants = {
  hidden: { opacity: 0, pathLength: 0 },
  visible: {
    opacity: 1,
    pathLength: 1,
    transition: { duration: 0.38, delay: 0.06 },
  },
};

function AnimatedTick() {
  return (
    <motion.svg
      aria-hidden="true"
      className="mt-0.5 h-6 w-6 shrink-0"
      variants={tickIconVariants}
      viewBox="0 0 24 24"
    >
      <motion.path
        d="M5.5 12.5l4 4 9-10"
        fill="none"
        stroke="#53b12c"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.6"
        variants={tickPathVariants}
      />
    </motion.svg>
  );
}

export function LandingPageV3() {
  useSeo({
    description:
      'AI app for car dealers to find deals faster, improve listing photos, and track exact profit per car with one operating system.',
    image: '/car-dealer-hero.png',
    keywords: [
      'car dealer app',
      'car deals finder',
      'ai car photography',
      'dealer profit tracking',
      'garage finance management',
      'used car dealer software',
    ],
    path: '/',
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        logo: '/logo.png',
        name: 'Profit Cars',
        url: '/',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        description:
          'Find better deals, improve listing visuals, and track profit with Profit Cars.',
        name: 'Profit Cars',
        url: '/',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        applicationCategory: 'BusinessApplication',
        description:
          'AI app for car dealers to source inventory, improve listings, and track margins.',
        name: 'Profit Cars',
        operatingSystem: 'Web',
      },
    ],
    title: 'Profit Cars | AI App for Car Dealers',
    type: 'website',
  });

  const [openFaqIndex, setOpenFaqIndex] = useState<number>(0);
  const dealerSliderLoop = [...dealerSliderLogos, ...dealerSliderLogos];
  const featuredBlogPostSlugs = ['hidden-costs-used-car-buying', 'how-ai-improves-car-sales'] as const;
  const featuredBlogPosts = featuredBlogPostSlugs
    .map((slug) => blogPosts.find((post) => post.slug === slug))
    .filter((post): post is (typeof blogPosts)[number] => Boolean(post));

  const handleHeroEarlyAccessClick = () => {
    document.getElementById('optin-get')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <main className="overflow-x-hidden bg-[#ececec] text-[#111827]">
      <section className="relative border-b border-black/10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url('/europe-map.svg')",
            backgroundPosition: '62% 38%',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '248%',
            filter: 'grayscale(1) brightness(0.42) contrast(0.95)',
            opacity: 0.24,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/90 via-[#efefef]/90 to-[#e3e3e3]/88" />

        <div className="relative mx-auto max-w-6xl px-6 pb-16 pt-4 sm:pb-20 lg:px-8">
          <GlobalNavbar className="mb-5" />
          <div className="flex flex-col items-center gap-5">
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="w-full max-w-2xl text-center"
              initial={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-[#53b12c]/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[#2f5b1b]">
                Made for EU Car dealers.
                <span aria-hidden="true" className="text-base leading-none">
                  🇪🇺
                </span>
              </div>

              <h1 className="font-display mt-5 text-4xl font-normal leading-[1.05] text-[#101010] sm:text-5xl lg:text-6xl">
                The all-in-one operating system for car dealers
              </h1>

              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-700">
                Built for EU dealers who want faster sourcing, cleaner listings, and clear margin visibility on every
                car they flip or sell.
              </p>
            </motion.div>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="w-full"
              initial={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.05 }}
            >
              <div className="relative mx-auto w-full max-w-[620px] pt-5 md:pt-8">
                <div className="relative mx-auto w-full max-w-[520px]">
                  <img
                    alt="Car dealer workflow preview"
                    className="aspect-[16/9] w-full object-contain mix-blend-multiply"
                    src={heroSectionImage}
                  />

                  <div className="pointer-events-none">
                    {heroToolPills.map((pill) => (
                      <span
                        key={pill}
                        className={cn(
                          'absolute inline-flex items-center gap-1.5 rounded-full border border-black/12 bg-white/80 px-3 py-1.5 text-center text-[11px] font-semibold text-[#111827] sm:gap-2 sm:px-4 sm:py-2 sm:text-sm',
                          heroPillPositions[pill]
                        )}
                      >
                        {pill === 'Deals Finder' && <SearchCheck className="h-3.5 w-3.5" />}
                        {pill === 'AI Photo' && <Camera className="h-3.5 w-3.5" />}
                        {pill === 'Garage' && <Warehouse className="h-3.5 w-3.5" />}
                        {pill === 'Finance' && <CircleDollarSign className="h-3.5 w-3.5" />}
                        {pill}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mx-auto mt-8 w-fit px-1 py-1">
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2.5">
                      {trustAvatars.map((avatar) => (
                        <img
                          alt={avatar.alt}
                          className="h-9 w-9 rounded-full border border-white object-cover shadow-[0_1px_2px_rgba(0,0,0,0.12)]"
                          key={avatar.src}
                          loading="lazy"
                          src={avatar.src}
                        />
                      ))}
                    </div>

                    <div className="min-w-0">
                      <p className="text-sm font-semibold leading-tight text-slate-700">
                        Early access for <span className="font-extrabold text-[#111827]">EU car dealers</span>
                      </p>

                      <div className="mt-1.5 inline-flex items-center gap-1.5">
                        <Star aria-hidden="true" className="h-3.5 w-3.5 fill-current text-[#f4b400]" />
                        <p className="text-xs font-semibold text-slate-700">Sourcing, photos, finance</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 26 }}
              transition={{ duration: 0.55, ease: 'easeOut', delay: 0.12 }}
            >
              <Button
                className="relative isolate mt-2 h-14 min-w-[280px] overflow-hidden rounded-[10px] border border-black/65 bg-[#08090c] px-9 font-bold tracking-[0.06em] text-white shadow-none hover:border-white/35 hover:bg-[#141820] focus-visible:outline-[#08090c] after:pointer-events-none after:absolute after:inset-y-0 after:-left-[55%] after:w-[42%] after:skew-x-[-22deg] after:bg-gradient-to-r after:from-transparent after:via-white/55 after:to-transparent after:opacity-0 after:transition-all after:duration-700 hover:after:left-[130%] hover:after:opacity-100"
                onClick={handleHeroEarlyAccessClick}
                type="button"
              >
                Get access
                <ArrowRight className="h-4 w-4" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="border-b border-black/10 bg-[#f2f2f2] py-8 sm:py-10">
        <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
          <p className="text-center text-lg font-semibold text-slate-700 sm:text-xl">
            Join the dealer waitlist for <span className="font-extrabold text-[#111827]">Profit Cars</span>
          </p>

          <div className="relative mt-5 overflow-hidden">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-[#f2f2f2] to-transparent sm:w-24" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-[#f2f2f2] to-transparent sm:w-24" />

            <motion.div
              animate={{ x: ['0%', '-50%'] }}
              className="flex w-max items-center gap-4"
              transition={{ duration: 30, ease: 'linear', repeat: Infinity }}
            >
              {dealerSliderLoop.map((logo, index) => (
                <div
                  className="flex h-14 min-w-[150px] shrink-0 items-center justify-center rounded-[14px] border border-black/10 bg-white px-5 sm:h-16 sm:min-w-[180px]"
                  key={`${logo.src}-${index}`}
                >
                  <img
                    alt={logo.alt}
                    className="max-h-9 w-auto object-contain sm:max-h-10"
                    loading="lazy"
                    src={logo.src}
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-[#f4f4f5] py-28 sm:py-32" id="tools">
        <div className="mx-auto w-full max-w-7xl px-6 text-center lg:px-8">
          <h2 className="mx-auto w-full max-w-full font-display text-5xl leading-[0.95] tracking-tight text-[#111317] sm:text-6xl lg:max-w-[600px]">
            Tools that give you <span className="font-extrabold">Your Unfair Advantage</span> in the car dealer
            industry in 2026.
          </h2>
        </div>
      </section>

      <section className="bg-[#f6f6f6] pb-20 pt-6 sm:pb-24 sm:pt-10">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <div className="space-y-14">
            <article className="grid gap-8 md:grid-cols-[0.92fr_1.08fr] md:gap-10">
              <div>
                <h3 className="font-display text-4xl leading-tight text-black sm:text-5xl">DEALS FINDER</h3>
                <motion.ul
                  className="mt-6 space-y-5 text-base leading-relaxed text-slate-700"
                  initial="hidden"
                  variants={benefitListVariants}
                  viewport={{ amount: 0.35, once: true }}
                  whileInView="visible"
                >
                  {dealsFinderBenefits.map((benefit) => (
                    <motion.li key={benefit} className="flex items-start gap-3" variants={benefitItemVariants}>
                      <AnimatedTick />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </div>

              <div className="flex items-center">
                <img
                  alt="Deals Finder interface preview"
                  className="h-auto w-full"
                  loading="lazy"
                  src={dealsFinderImage}
                />
              </div>
            </article>

            <div className="relative flex items-center">
              <div className="h-px w-full bg-black/15" />
              <span className="absolute left-1/2 inline-flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-black/20 bg-white text-slate-700">
                <Plus className="h-4 w-4" />
              </span>
            </div>

            <article className="grid gap-8 md:grid-cols-[1fr_1fr] md:gap-10">
              <div>
                <h3 className="font-display text-4xl leading-tight text-black sm:text-5xl">
                  AI PHOTOGRAPHY THAT MAKES EVERY LISTING STAND OUT.
                </h3>
                <p className="mt-5 text-base leading-relaxed text-slate-700">Images that make your ad/listing stand out.</p>

                <motion.ul
                  className="mt-5 space-y-4 text-base leading-relaxed text-slate-700"
                  initial="hidden"
                  variants={benefitListVariants}
                  viewport={{ amount: 0.35, once: true }}
                  whileInView="visible"
                >
                  {aiPhotographyBenefits.map((benefit) => (
                    <motion.li key={benefit} className="flex items-start gap-3" variants={benefitItemVariants}>
                      <AnimatedTick />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </div>

              <div className="rounded-[24px] border border-black/10 bg-[#f3f4f6] p-4">
                <div className="relative aspect-[75/56] overflow-hidden rounded-[18px] border border-black/15 bg-black">
                  <ReactCompareSlider
                    className="h-full w-full"
                    defaultPosition={56}
                    onlyHandleDraggable={false}
                    transition="0.24s cubic-bezier(0.22,1,0.36,1)"
                    itemOne={
                      <ReactCompareSliderImage
                        alt="Before listing visual"
                        src={aiBeforeImage}
                        style={{ filter: 'brightness(0.78) saturate(0.65)', height: '100%', objectFit: 'cover', width: '100%' }}
                      />
                    }
                    itemTwo={
                      <ReactCompareSliderImage
                        alt="After listing visual"
                        src={aiAfterImage}
                        style={{ height: '100%', objectFit: 'cover', width: '100%' }}
                      />
                    }
                  />

                  <div className="pointer-events-none absolute left-4 top-4 z-20 whitespace-nowrap rounded-full bg-black/70 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-white">
                    Before
                  </div>
                  <div className="pointer-events-none absolute right-4 top-4 z-20 whitespace-nowrap rounded-full bg-[#53b12c]/90 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-black">
                    After
                  </div>
                </div>
              </div>
            </article>

            <div className="relative flex items-center">
              <div className="h-px w-full bg-black/15" />
              <span className="absolute left-1/2 inline-flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-black/20 bg-white text-slate-700">
                <Plus className="h-4 w-4" />
              </span>
            </div>

            <article className="grid gap-8 md:grid-cols-[1fr_1fr] md:gap-10">
              <div>
                <h3 className="font-display text-4xl leading-tight text-black sm:text-5xl">
                  GARAGE AND FINANCE MANAGEMENT.
                </h3>
                <p className="mt-5 text-base leading-relaxed text-slate-700">
                  Know your exact profit on every car — before and after the sale.
                </p>
                <motion.ul
                  className="mt-6 space-y-4 text-base leading-relaxed text-slate-700"
                  initial="hidden"
                  variants={benefitListVariants}
                  viewport={{ amount: 0.35, once: true }}
                  whileInView="visible"
                >
                  {financeManagementBenefits.map((benefit) => (
                    <motion.li key={benefit} className="flex items-start gap-3" variants={benefitItemVariants}>
                      <AnimatedTick />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </div>

              <div className="flex min-h-[280px] items-center justify-end">
                <img
                  alt="Finance workflow on laptop"
                  className="h-auto w-full max-w-[560px] object-contain"
                  loading="lazy"
                  src={financeLaptopImage}
                />
              </div>
            </article>
          </div>

        </div>
      </section>

      <OptInSection id="optin-get" source="hero" />

      <section className="bg-[#f3f3f3] py-20 sm:py-24" id="testimonials">
        <div className="mx-auto w-full max-w-[1200px] px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-5xl leading-[0.95] text-black sm:text-6xl">
              <span className="font-normal tracking-[0.01em]">Car Dealers</span>{' '}
              <span className="font-extrabold italic">love Profit Cars</span>
            </h2>
          </div>

          <div className="mt-12 columns-1 gap-4 md:columns-2 xl:columns-3">
            {testimonials.map((item, index) => (
              <motion.article
                className="mb-4 break-inside-avoid rounded-[16px] border border-black/10 bg-white p-5"
                initial={{ opacity: 0, y: 18 }}
                key={item.name}
                transition={{ delay: index * 0.05, duration: 0.36, ease: 'easeOut' }}
                viewport={{ amount: 0.2, once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <img
                      alt={`${item.name} testimonial avatar`}
                      className="h-12 w-12 rounded-full border border-black/10 object-cover"
                      loading="lazy"
                      src={item.avatar}
                    />

                    <div className="min-w-0">
                      <p className="truncate text-2xl font-bold leading-none text-black">{item.name}</p>
                      <p className="mt-1 truncate text-sm text-slate-500">{item.meta}</p>
                    </div>
                  </div>

                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                    <Mail className="h-4 w-4" />
                  </span>
                </div>

                <p className="mt-5 text-base leading-relaxed text-slate-800">{item.quote}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#efefef] py-20 sm:py-24" id="faq">
        <div className="mx-auto w-full max-w-5xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#2f5b1b]">FAQ</p>
            <h2 className="mt-4 font-display text-5xl leading-[0.95] text-black sm:text-6xl">
              Questions dealers ask before joining
            </h2>
          </div>

          <div className="mx-auto mt-10 max-w-4xl space-y-3">
            {faqItems.map((item, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <article
                  className={cn(
                    'rounded-[16px] border border-black/10 bg-white px-5 py-4 transition',
                    isOpen && 'border-black/20'
                  )}
                  key={item.question}
                >
                  <button
                    className="flex w-full items-center justify-between gap-4 text-left"
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                    type="button"
                  >
                    <span className="text-lg font-bold text-black sm:text-xl">{item.question}</span>
                    <span
                      className={cn(
                        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-black/20 text-black transition',
                        isOpen && 'rotate-45'
                      )}
                    >
                      <span className="relative h-3 w-3">
                        <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-black" />
                        <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-black" />
                      </span>
                    </span>
                  </button>

                  {isOpen && <p className="mt-3 pr-10 text-base leading-relaxed text-slate-700">{item.answer}</p>}
                </article>
              );
            })}
          </div>

          <div className="mt-10 flex justify-center">
            <Button
              className="relative isolate mt-2 h-14 min-w-[280px] overflow-hidden rounded-[10px] border border-black/65 bg-[#08090c] px-9 font-bold tracking-[0.06em] text-white shadow-none hover:border-white/35 hover:bg-[#141820] focus-visible:outline-[#08090c] after:pointer-events-none after:absolute after:inset-y-0 after:-left-[55%] after:w-[42%] after:skew-x-[-22deg] after:bg-gradient-to-r after:from-transparent after:via-white/55 after:to-transparent after:opacity-0 after:transition-all after:duration-700 hover:after:left-[130%] hover:after:opacity-100"
              onClick={handleHeroEarlyAccessClick}
              type="button"
            >
              Get access
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-[#f6f6f6] py-20 sm:py-24" id="insights">
        <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-5xl leading-[0.95] text-black sm:text-6xl">
              insights every car dealer should know
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {featuredBlogPosts.map((post) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                key={post.slug}
                transition={{ duration: 0.42, ease: 'easeOut' }}
                viewport={{ amount: 0.2, once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <Link
                  className="group block h-full overflow-hidden rounded-[24px] border border-black/10 bg-white transition hover:border-black/25"
                  to={`/blog/${post.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-black/5">
                    <img
                      alt={post.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                      loading="lazy"
                      src={post.image}
                    />
                  </div>

                  <div className="p-6">
                    <h3 className="text-2xl font-extrabold leading-tight text-black sm:text-3xl">{post.title}</h3>
                    <p className="mt-3 text-base leading-relaxed text-slate-700">{post.description}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.09em] text-[#2f5b1b]">
                      Read article
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link
              className="inline-flex h-12 items-center justify-center rounded-[10px] border border-black/65 bg-[#08090c] px-8 text-sm font-bold uppercase tracking-[0.06em] text-white transition hover:border-white/35 hover:bg-[#141820]"
              to="/blog"
            >
              See all blog posts
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-black/10 bg-[#e7e7e7]">
        <div className="mx-auto w-full max-w-6xl px-6 py-10 lg:px-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-display text-3xl leading-none text-black sm:text-4xl">Profit Cars</p>
              <p className="mt-2 text-sm text-slate-600">Operating system for modern car dealers.</p>
            </div>

            <nav className="flex flex-wrap items-center gap-4 text-sm font-semibold text-slate-700">
              <a className="transition hover:text-black" href="#tools">
                Features
              </a>
              <a className="transition hover:text-black" href="#optin-get">
                Get Access
              </a>
              <a className="transition hover:text-black" href="#faq">
                FAQ
              </a>
              <Link className="transition hover:text-black" to="/blog">
                Blog
              </Link>
              <Link className="transition hover:text-black" to="/quiz">
                Quiz
              </Link>
            </nav>
          </div>

          <div className="mt-8 border-t border-black/10 pt-5 text-xs text-slate-500">
            © {new Date().getFullYear()} Profit Cars. All rights reserved.
          </div>
        </div>
      </footer>
    </main>
  );
}
